// components/AboutUs.jsx
import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function AboutUs() {
    return (
        <section className="py-20 bg-gray-50">
            <Helmet>
                <title>About Us | Arxis Technologies Limited</title>
                <meta name="description" content="Arxis Accounting is an all-in-one financial and business management platform built to streamline, bookkeeping, invoicing and automated tax compliance for businesses." />
            </Helmet>
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                {/* Company Intro */}
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                        About Arxis Technologies Limited
                    </h1>
                    <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                        Arxis Technologies Limited is a Nigerian fintech company dedicated to simplifying financial management for sole proprietors, small businesses, and growing enterprises across the country.
                    </p>
                    <p className="mt-6 text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
                        We created Arxis Accounting — an all-in-one platform that brings precise, compliant, and automated bookkeeping within reach of every Nigerian business owner.
                        From smart invoicing and effortless VAT & withholding tax filing to real-time cash flow visibility, payroll automation, and bank-grade security, our mission is to remove the complexity from accounting so you can focus on what truly matters: building and growing your business.
                    </p>
                </div>

                {/* Mission & Vision */}
                <div className="grid md:grid-cols-2 gap-12 mb-20">
                    <div className="bg-white p-10 rounded-2xl shadow-lg border border-gray-100">
                        <h2 className="text-3xl font-bold mb-6 text-[#4285F4]">Our Mission</h2>
                        <p className="text-gray-700 text-lg leading-relaxed">
                            To deliver powerful yet affordable accounting software that automates routine financial tasks, ensures full compliance with Nigerian regulations (including direct FIRS submissions),
                            and provides actionable insights — empowering entrepreneurs to run their businesses with confidence, accuracy, and efficiency.
                        </p>
                    </div>

                    <div className="bg-white p-10 rounded-2xl shadow-lg border border-gray-100">
                        <h2 className="text-3xl font-bold mb-6 text-[#4285F4]">Our Vision</h2>
                        <p className="text-gray-700 text-lg leading-relaxed">
                            To become the trusted financial backbone for millions of African small and medium-sized businesses — a platform that grows with you, from your first invoice to managing multiple entities with enterprise-grade features and integrations.
                        </p>
                    </div>
                </div>

                {/* Core Values */}
                <div className="mb-20">
                    <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
                        What We Stand For
                    </h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
                            <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="text-4xl">🚀</span>
                            </div>
                            <h3 className="text-2xl font-semibold mb-4 text-gray-800">Innovation</h3>
                            <p className="text-gray-600 text-lg">
                                We continuously improve our platform with intelligent automation, AI-powered insights, and features that anticipate the real needs of Nigerian businesses.
                            </p>
                        </div>

                        <div className="text-center p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
                            <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="text-4xl">🔒</span>
                            </div>
                            <h3 className="text-2xl font-semibold mb-4 text-gray-800">Security & Compliance</h3>
                            <p className="text-gray-600 text-lg">
                                Protecting your financial data with enterprise-level encryption while making regulatory compliance (VAT, withholding tax, payroll) simple and stress-free.
                            </p>
                        </div>

                        <div className="text-center p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
                            <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="text-4xl">💼</span>
                            </div>
                            <h3 className="text-2xl font-semibold mb-4 text-gray-800">Accessibility</h3>
                            <p className="text-gray-600 text-lg">
                                Offering transparent, VAT-inclusive pricing and scalable plans that make professional-grade accounting tools available to businesses of every size — from sole traders to growing teams.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Final CTA */}
                <div className="text-center pt-8">
                    <h2 className="text-3xl font-bold mb-6 text-gray-900">
                        Ready to Experience Smarter Accounting?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Join thousands of Nigerian business owners who trust Arxis to keep their books accurate, compliant, and effortless.
                    </p>
                    <button className="px-10 py-5 bg-[#4285F4] text-white font-semibold text-lg rounded-xl hover:bg-[#336bd1] transition shadow-lg">
                        Start Your Free Trial
                    </button>
                </div>
            </div>
        </section>
    );
}